import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/v1',
  withCredentials: true, // sends the httpOnly refresh-token cookie
});

// In-memory access token store. Never persisted to localStorage/sessionStorage —
// the refresh token (httpOnly cookie) is what survives a page reload; on reload
// AuthContext calls /auth/refresh once to get a fresh access token back into memory.
let accessToken = null;
let onUnauthorized = null;

// VIEW-AS v1: admin "view as <Ref. Party user>". Kept in sessionStorage (this tab only) and sent as X-View-As;
// the server honours it only for an admin token, and only read-only.
export function getViewAs() {
  try {
    const pid = sessionStorage.getItem('viewAsPartyId'); // VIEW-AS-PARTY v1: a whole Ref. Party (no login user needed)
    if (pid) return { id: pid, name: sessionStorage.getItem('viewAsUserName') || '', party: true };
    const id = sessionStorage.getItem('viewAsUserId');
    return id ? { id, name: sessionStorage.getItem('viewAsUserName') || '' } : null;
  } catch (e) { return null; }
}
export function startViewAs(id, name, party) {
  try {
    sessionStorage.removeItem('viewAsUserId'); sessionStorage.removeItem('viewAsPartyId');
    sessionStorage.setItem(party ? 'viewAsPartyId' : 'viewAsUserId', String(id)); sessionStorage.setItem('viewAsUserName', name || '');
  } catch (e) { /* ignore */ }
  window.location.assign('/');
}
export function clearViewAs() {
  try { sessionStorage.removeItem('viewAsUserId'); sessionStorage.removeItem('viewAsPartyId'); sessionStorage.removeItem('viewAsUserName'); sessionStorage.removeItem('myReport'); } catch (e) { /* ignore */ }
}
// OWN-PARTY v1: "My report" switch for a full-access user who is also a Ref. Party (this tab only).
export function getMyReport() {
  try { return sessionStorage.getItem('myReport') === '1'; } catch (e) { return false; }
}
export function setMyReport(on) {
  try { if (on) sessionStorage.setItem('myReport', '1'); else sessionStorage.removeItem('myReport'); } catch (e) { /* ignore */ }
  window.location.assign('/');
}
export function stopViewAs() {
  clearViewAs();
  window.location.assign('/');
}

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

/** Registered by AuthContext so a failed refresh can force a logout/redirect. */
export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

apiClient.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  const va = getViewAs();
  if (va) config.headers[va.party ? 'X-View-As-Party' : 'X-View-As'] = va.id;
  else if (getMyReport()) config.headers['X-My-Report'] = '1';
  return config;
});

let refreshPromise = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response && error.response.status;

    // Attempt exactly one silent refresh-and-retry per request on a 401,
    // never on the refresh/login endpoints themselves (avoid infinite loops).
    const isAuthEndpoint = original && /\/auth\/(login|refresh)$/.test(original.url || '');

    if (status === 401 && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      try {
        refreshPromise = refreshPromise || apiClient.post('/auth/refresh');
        const { data } = await refreshPromise;
        refreshPromise = null;
        setAccessToken(data.data.accessToken);
        original.headers.Authorization = `Bearer ${data.data.accessToken}`;
        return apiClient(original);
      } catch (refreshErr) {
        refreshPromise = null;
        setAccessToken(null);
        if (onUnauthorized) onUnauthorized();
      }
    }

    // Normalize every failure to { code, message } per ERROR_HANDLING_STANDARDS.md
    if (error.response && error.response.data && error.response.data.error) {
      return Promise.reject(error.response.data.error);
    }
    return Promise.reject({ code: 'NETWORK_ERROR', message: 'Unable to reach the server.' });
  },
);

export default apiClient;
